import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { API_BASE } from '../../../lib/api-base';
import { useBasePath } from '../../../lib/use-base-path';
import { useTranslation } from '../../../lib/i18n';
import { waitForRecipientVisible } from '../../../lib/wait-for-recipient';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { DashboardLoading } from '../DashboardView/DashboardLoading';
import { SignupShell } from './SignupShell';
import { Button } from '../ui';
import styles from './CreateRecipientOnEnter.module.css';

// Kept in step with `RECIPIENT_CREATED_COOKIE` in lib/onboarding-gate.js — a CommonJS custom-server
// module the client can't import. Set the instant the create returns, just before the navigation home: the
// gate reads it and RENDERS for this just-created caller instead of bouncing them back here while
// `getMyRecipient`'s search index still lags the fresh metasite. UX hint, not a credential (the gate is
// "UX routing, not authorization"), so a JS-set, non-HttpOnly cookie is correct.
const RECIPIENT_CREATED_COOKIE = 'symphony-recipient-created';
// Long enough to cover a slow index, short enough that a genuinely tenant-less caller (who did NOT just
// create) is never waved through.
const RECIPIENT_CREATED_TTL_S = 180;

// No fetch here may hang unbounded: this screen's ONLY states are "loading" and "failed", so a request
// that never settles is a spinner that never ends and never offers retry — the exact signup hang this
// flow shipped. A timeout aborts into the catch, which shows the retry instead.
const GET_TIMEOUT_MS = 15000;
const CREATE_TIMEOUT_MS = 30000;
// Best-effort settle on a resolved tenant when the index is quick; the caller navigates regardless once
// it elapses (the cookie above keeps the gate from bouncing), so this only shapes the fast path.
const RECIPIENT_VISIBLE_BUDGET_MS = 30000;

async function fetchWithTimeout(input: string, init: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

function markRecipientJustCreated(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${RECIPIENT_CREATED_COOKIE}=1; Max-Age=${RECIPIENT_CREATED_TTL_S}; Path=/; SameSite=Lax`;
}

/**
 * Onboarding's first step: give the caller a recipient, then send them into their conversation.
 *
 * The product asks for a number inside the conversation now, so there is nothing to collect here. But a
 * conversation is tenant-scoped and cannot exist before a recipient does, so somebody has to create one,
 * and this is the only point in the flow that is reached exactly once per new user.
 *
 * Deliberately not done in the onboarding gate. That gate is best-effort with a 1500ms budget and renders
 * on any failure, so a slow create would hand the app to a caller with no tenant and every tenant-scoped
 * call would fail; and `getMyRecipient` reads an eventually-consistent index, so a gate that created would
 * redirect before it could read its own write.
 */
export function CreateRecipientOnEnter() {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const { setActiveConversationId } = useActiveConversation();
  const [failed, setFailed] = useState(false);
  // React 18 mounts effects twice in development. Creating is idempotent for the same email, but the
  // poll that follows is not free, so the second pass is skipped rather than raced.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void create();
    // `create` closes over nothing that changes for the life of this screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function create() {
    setFailed(false);
    try {
      // Someone who already has a recipient does not belong in a create. This route stays in the caller's
      // history, so the back button lands them here again — and creating is harmless (the service matches on
      // email alone and writes nothing) but burning the rename-notice flag below is not: it would take the
      // notice away from a returning user who has never seen it. Send them home instead, without the
      // greeting entry, which is for someone arriving for the first time.
      const existing = await fetchWithTimeout(`${API_BASE}/api/recipient`, { credentials: 'include' }, GET_TIMEOUT_MS);
      if (existing.ok) {
        // "Has a recipient" is NOT "has onboarded". The create can land a recipient whose greeting never
        // fired (the signup hang did exactly this), and a returning user's back button lands here too. Gate
        // on onboarding status, not existence: an onboarded caller gets the quiet home; a recipient that
        // exists but never onboarded must still be greeted, or it's stranded on the bare home forever.
        const onboarded = await existing
          .json()
          .then((body) => (body as { recipient?: { finishedOnboarding?: boolean } })?.recipient?.finishedOnboarding === true)
          .catch(() => false);
        if (onboarded) {
          // A returning, onboarded user. Quiet home.
          void router.replace(`${basePath}/`);
          return;
        }
        // Recipient exists but onboarding never happened. Send them home; the client derives the kickoff
        // from recipient state (the native onboardingRoute port) and Maestro speaks first — no `welcome`
        // param needed.
        setActiveConversationId(null);
        void router.replace(`${basePath}/`);
        return;
      }
      const res = await fetchWithTimeout(
        `${API_BASE}/api/recipient`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ withoutPhone: true }),
        },
        CREATE_TIMEOUT_MS,
      );
      if (!res.ok) throw new Error(`${res.status}`);
      // Tell the server gate a recipient was just created, so the hard load below RENDERS instead of
      // bouncing this caller back here while getMyRecipient's index still lags the fresh metasite. This is
      // what breaks the create→navigate→bounce loop; the wait below is now only a nicety.
      markRecipientJustCreated();
      // Best-effort: settle on a resolved tenant when the index is quick, but NEVER dead-end on it. The
      // read is eventually-consistent, so waiting on it used to mean "expired deadline → failed signup for
      // a recipient that already exists" (or, on a stalled request, a spinner with no retry). Navigate
      // regardless once this returns — the cookie above lets the gate render, so a slow or stalled index
      // becomes "in the app, tenant calls retrying" rather than a signup that never completes. This mirrors
      // the phone/code paths, which also discard the boolean and navigate.
      await waitForRecipientVisible(API_BASE, RECIPIENT_VISIBLE_BUDGET_MS);
      setActiveConversationId(null);
      // Home, no greeting param: the client derives the onboarding kickoff from recipient state and greets.
      void router.replace(`${basePath}/`);
    } catch (err) {
      console.warn('[onboarding-create]', err);
      setFailed(true);
    }
  }

  if (failed) {
    return (
      <SignupShell>
        <div className={styles.failure}>
          <p className={styles.error} role="alert">
            {t('onboarding.phone.error.generic')}
          </p>
          <Button type="button" size="large" fullWidth onClick={() => void create()}>
            {t('common.retry')}
          </Button>
        </div>
      </SignupShell>
    );
  }
  return (
    <SignupShell>
      <DashboardLoading />
    </SignupShell>
  );
}
